import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import Icon, { ICONS, COLORS } from './icon';
import { InvitedPresenter } from '../app-entry/producer/meeting-modal';
import SelectUsersRow from './select-users-row';
import Avatar from './avatar';

export interface SelectUserProps {
	users: InvitedPresenter[],
	onChange: (users: InvitedPresenter[]) => void;
	selected: InvitedPresenter[];
	disabled?: boolean;
	dropdownPosition?: string;
	single?: boolean;
}

export default function SelectUsers(props: SelectUserProps) {
	const {
		users,
		onChange,
		selected,
		disabled,
		dropdownPosition,
		single
	} = props;

	const [open, setOpen] = useState(false);
	const [activeClass, setActiveClass] = useState('closed');
	const [selectAll, setSelectAll] = useState(false);

	useEffect(() => {
		if (selected.length === users.length && !selectAll) setSelectAll(true);
	}, [selected]);

	function animationFinished() {
		if (open && activeClass === 'closed') {
			setOpen(false);
		}
	}

	function close() {
		setActiveClass('closed');
	}

	function _open() {
		setOpen(true);
		setTimeout(() => setActiveClass('open'), 16);
	}

	function handleChange(option: InvitedPresenter | null) {
		setSelectAll(false);
		if (option === null) {
			onChange([]);
		} else if (selected.find((selectedUser) => selectedUser.email === option.email)) {
			onChange(selected.filter(opt => opt.email !== option.email));
		} else if (single) {
			onChange([option]);
		} else if (selected.length + 1 === users.length) {
			toggleSelectAll();
		} else {
			onChange([...selected, option]);
		}		

		if (single) {
			setTimeout(() => close(), 30);
		}
	}

	function toggleSelectAll() {
		if (selectAll) {
			onChange([]);
			setSelectAll(false);
		} else {
			onChange(users)
			setSelectAll(true);
		}
	}

	function selectedUserNameOrEmail(user: InvitedPresenter) {
		if (user.first_name) {
			if (user.last_name) {
				return `${user.first_name} ${user.last_name}`;
			}
			return user.first_name
		} else {
			return user.email
		}
	}

	useEffect(() => {
		const selectUsersElement = document.getElementsByClassName('select-users-body')[0];

		function closeDropdownIfClickOutside(event: any) {
			var isClickInside = selectUsersElement.contains(event.target);

			if (!isClickInside) {
				// User clicked outside select users dropdown, close it 
				close();
			}
		}

		document.addEventListener('click', closeDropdownIfClickOutside);
		return () => document.removeEventListener("click", closeDropdownIfClickOutside);
	}, [])

	const labelBoxRef = React.useRef<HTMLDivElement>(null);

	return (
		<div className={classNames("select-users", { open, disabled, single })}>
			<div className="select-users-label" onClick={() => open ? close() : _open()} ref={labelBoxRef}>
				<div className="selected-users">
					{(selectAll && !single) ? (
						<span className='bl-font-l3 all-placeholder'>All</span>
					) : (
						selected.length > 0 && selected.map((user, index) => <span key={index} className='bl-font-l3 selected-user-chip '>
							<Avatar user={user} size={'xsmall'} noBorder={true} />{selectedUserNameOrEmail(user)}
						</span>)
					)}
				</div>
				{(selected.length === 0 && single) ? <span className='bl-font-l3 selected-user-chip single-placeholder flex-center'>
					<div className='question-mark flex-center'>
						<Icon name={ICONS.QUESTION} size={12} color={COLORS.TEXT_GRAY_300}/>
					</div><span>SELECT PRESENTER</span>
				</span> : null}
				<div className={`open-users ${open ? 'is-open' : ''}`}>
					<Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={COLORS.DEFAULT_GRAY} size={10} />
				</div>
			</div>
			<div 
				onClick={_open} 
				className={classNames("select-users-body", activeClass, dropdownPosition)}
				onTransitionEnd={animationFinished}
				style={{top: `${(labelBoxRef?.current?.offsetHeight ?? 28) + 4}px`}}
			>
				{single ? (
					<div className="select-users-option suggested-label">
						<label className='bl-font-l3'>SUGGESTED</label>
					</div>
				) : (
					<div
						onClick={toggleSelectAll}
						className={classNames("select-users-option checkbox", { selected: selectAll })}>
						<label className='margin-right-medium'>All</label>
						<div className="checkbox-outer"><FontAwesomeIcon icon={faCheck} /></div>
					</div>
				)}
				{users.map((user) => (
					<SelectUsersRow
						user={user}
						key={`${user.gr_user}-${user.email}`}
						handleChange={handleChange}
						selected={!!selected.find((s) => s.email === user.email)}
						single={single}
					/>
				))}
				{single ? <div onClick={() => handleChange(null)} className="select-users-option checkbox">
					<div className='question-mark sm flex-center'>
						<Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE}/>
					</div>
					<label className='first-label bl-font-p2 truncate-overflow'>Custom</label>
				</div> : null}
			</div>
		</div>
	)
}
